import routes from '../../routes';
import { bandittoApi } from '../../store/api/api';
import { SearchType } from '../search/search.types';
import {
  Consumer,
  ConsumerBilling,
  ConsumerIntegration,
  ReferralsInfo,
  TokenAdjustment,
  TokenBalance,
} from './consumer.types';
import { ConsumerMessage } from '../message/message.types';
import { mergeConsumerMessages } from '../message/message.utils';
import { PaginationRequest, PaginationRequestById } from '../../type';
import { Log } from '../log/logs.types';
import {
  Language,
  LanguageRequest,
  LanguagesInfo,
  SettingDto,
  UpdateSettingRequest,
} from './settings.types';

export interface BlockUnblockRequest {
  id: string;
  message: string;
}

interface ConsumerTransactionRequest {
  amount: number;
  typeOperation: string;
  id: string;
}
interface ConsumerTokenAdjustmentRequest extends TokenAdjustment {
  id: string;
}
interface ConsumerBalance {
  amount: number;
}

interface LogsPaginationRequest extends PaginationRequest {
  consumerId: string;
  subjectId?: string;
  objectId?: string;
  startDateTime?: string;
  endDateTime?: string;
}

interface ConsumerIntegrationsRequest extends PaginationRequest {
  consumerId: string;
}

interface ConsumerReferralsRequest extends PaginationRequest {
  consumerId: string;
}
export const CONSUMER_MESSAGES_PAGE_SIZE = 10;

/**
 * `latest` keeps polling the newest messages, `history` accumulates the older ones.
 * Both live in their own cache entry, so a loaded history window can never shift
 * the polled args away from the newest messages.
 */
export type ConsumerMessagesMode = 'latest' | 'history';

type ConsumerMessagesRequest = {
  id: string;
  size: number;
  mode: ConsumerMessagesMode;
  /** Cursor for `history`: the `createdAt` of the oldest message on screen. */
  before?: string;
};

const consumerApi = bandittoApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllConsumers: builder.query<Consumer[], SearchType>({
      query: ({
        size = 10,
        sortField = '',
        page = 0,
        sortOrder = 1,
        filter = '',
        filterField = 'email',
      }) => ({
        url: routes.api.getAllConsumers(),
        params: {
          size,
          sortField,
          sortOrder,
          filter,
          filterField,
          page,
        },
      }),
      providesTags: ['Consumer', 'Consumers'],
    }),
    getConsumerMagicLink: builder.mutation<{ magicLinkCode: string }, { consumerId: string }>({
      query: ({ consumerId }) => ({
        url: routes.api.getConsumerMagicLink(consumerId),
        method: 'GET',
      }),
    }),

    getConsumerById: builder.query<Consumer, string>({
      query: (id: string) => routes.api.aboutConsumerById(id),
      providesTags: ['Consumer', 'Consumers'],
    }),
    lockConsumerById: builder.mutation<Consumer, BlockUnblockRequest>({
      query: ({ id, message }) => ({
        url: routes.api.lockConsumerById(id),
        method: 'PATCH',
        body: { message },
      }),
      invalidatesTags: ['Consumer'],
    }),
    unlockConsumerById: builder.mutation<Consumer, BlockUnblockRequest>({
      query: ({ id, message }) => ({
        url: routes.api.unlockConsumerById(id),
        method: 'PATCH',
        body: { message },
      }),
      invalidatesTags: ['Consumer'],
    }),
    getMessagesByConsumerId: builder.query<ConsumerMessage[], ConsumerMessagesRequest>({
      query: ({ id, size = CONSUMER_MESSAGES_PAGE_SIZE, before }) => {
        const params: Record<string, string | number | boolean> = { size, loggerAnEvent: false };
        if (before) {
          params.before = before;
        }
        return {
          url: routes.api.consumerMessages(id),
          params,
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${queryArgs.id}-${queryArgs.mode})`,
      // the server answers newest first, the cache entry stays oldest first;
      // `merge` is skipped for the very first response, so sort here as well
      transformResponse: (messages: ConsumerMessage[]) => mergeConsumerMessages(messages),
      merge: (currentCache, newMessages) => mergeConsumerMessages(currentCache, newMessages),
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.before !== previousArg?.before,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Messages' as const, id })),
              { type: 'Messages', id: 'LIST' },
            ]
          : [{ type: 'Messages', id: 'LIST' }],
    }),
    /**
     * The backend logs the OPEN_DIALOG event as a side effect of reading messages
     * (`loggerAnEvent`), so the event needs a request of its own: the accumulating
     * queries above are polled and must not log an event every 15 seconds.
     * The response is intentionally minimal and untagged, so nothing refetches it.
     */
    logOpenDialog: builder.query<ConsumerMessage[], string>({
      query: (id) => ({
        url: routes.api.consumerMessages(id),
        params: {
          page: 0,
          size: 1,
          loggerAnEvent: true,
        },
      }),
    }),
    getAllConsumerOperationById: builder.query<ConsumerBilling[], PaginationRequestById>({
      query: ({ id, page = 0, size = 10 }) => ({
        url: routes.api.consumerOperation(id),
        params: {
          page,
          size,
        },
      }),
      providesTags: ['Operation'],
    }),
    makeConsumerTransaction: builder.mutation<ConsumerBilling, ConsumerTransactionRequest>({
      query: ({ id, amount, typeOperation }) => ({
        url: routes.api.consumerOperation(id),
        method: 'POST',
        body: { amount, typeOperation },
      }),
      invalidatesTags: ['Operation'],
    }),
    adjustConsumerTokens: builder.mutation<TokenBalance, ConsumerTokenAdjustmentRequest>({
      query: ({ id, direction, amount, comment }) => ({
        url: routes.api.consumerTokens(id),
        method: 'POST',
        body: { direction, amount, comment },
      }),
      invalidatesTags: ['Operation'],
    }),
    getConsumerBalance: builder.query<ConsumerBalance, string>({
      query: (id) => routes.api.consumerBalance(id),
      providesTags: ['Operation'],
    }),
    getConsumerIntegrations: builder.query<ConsumerIntegration[], ConsumerIntegrationsRequest>({
      query: ({ consumerId, page = 0, size = 10 }) => ({
        url: routes.api.consumerIntegrations(consumerId),
        params: {
          page,
          size,
        },
      }),
      providesTags: ['Integrations'],
    }),
    getConsumerReferrals: builder.query<ReferralsInfo, ConsumerReferralsRequest>({
      query: ({ consumerId, page, size }) => ({
        url: routes.api.consumerReferrals(consumerId),
        params: {
          page,
          size,
        },
      }),
      // a decision on the rewards page changes the rewards this tab shows
      providesTags: ['ReferralRewards'],
    }),
    getConsumerLogsById: builder.query<Log[], LogsPaginationRequest>({
      query: (params) => ({
        url: routes.api.getLogsByConsumerId(params.consumerId),
        params,
      }),
      providesTags: ['Logs'],
    }),
    reset2FAForConsumerById: builder.mutation<Consumer, { consumerId: string }>({
      query: ({ consumerId }) => ({
        url: routes.api.resetConsumer2FAById(consumerId),
        method: 'PATCH',
      }),
      invalidatesTags: ['Consumer'],
    }),
    getAllLanguages: builder.query<LanguagesInfo, void>({
      query: () => routes.api.consumersLanguages(),
      providesTags: ['Languages'],
    }),
    createLanguage: builder.mutation<Language, LanguageRequest>({
      query: (language) => ({
        url: routes.api.consumersLanguages(),
        method: 'POST',
        body: language,
      }),
      invalidatesTags: ['Languages'],
    }),
    updateLanguage: builder.mutation<Language, LanguageRequest>({
      query: ({ id, name }) => ({
        url: routes.api.consumersLanguageById(id),
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: ['Languages'],
    }),
    deleteLanguage: builder.mutation<{ success: boolean }, string>({
      query: (languageId) => ({
        url: routes.api.deleteConsumersLanguage(languageId),
        method: 'PATCH',
      }),
      invalidatesTags: ['Languages'],
    }),
    restoreLanguage: builder.mutation<{ success: boolean }, string>({
      query: (languageId) => ({
        url: routes.api.restoreConsumersLanguage(languageId),
        method: 'PATCH',
      }),
      invalidatesTags: ['Languages'],
    }),
    getAllSettings: builder.query<SettingDto[], void>({
      query: () => routes.api.consumersSettings(),
      providesTags: ['Settings'],
    }),
    updateSettingById: builder.mutation<SettingDto, UpdateSettingRequest>({
      query: ({ id, property, gwType, common }) => ({
        url: routes.api.consumersSettingsById(id),
        method: 'PATCH',
        body: { property, gwType, common },
      }),
      invalidatesTags: ['Settings'],
    }),
  }),
});

export const {
  useGetAllConsumersQuery,
  useGetConsumerByIdQuery,
  useLockConsumerByIdMutation,
  useUnlockConsumerByIdMutation,
  useGetMessagesByConsumerIdQuery,
  useLogOpenDialogQuery,
  useLazyGetAllConsumerOperationByIdQuery,
  useMakeConsumerTransactionMutation,
  useAdjustConsumerTokensMutation,
  useGetConsumerBalanceQuery,
  useGetConsumerLogsByIdQuery,
  useLazyGetConsumerIntegrationsQuery,
  useGetConsumerReferralsQuery,
  useReset2FAForConsumerByIdMutation,
  useGetAllLanguagesQuery,
  useCreateLanguageMutation,
  useUpdateLanguageMutation,
  useDeleteLanguageMutation,
  useRestoreLanguageMutation,
  useGetAllSettingsQuery,
  useUpdateSettingByIdMutation,
  useGetConsumerMagicLinkMutation,
} = consumerApi;
